import { useMemo } from 'react';
import { Granit } from 'src/services/granits';
import { SortType } from './types';
import { getDimensionsWithGeometry } from 'src/utils/3d.utils';

export const useActiveGranits = (
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
) => {
  return useMemo(() => {
    const allPieces = [
      ...activePieces,
      ...customSteles,
      ...customPlatings,
      ...customSoles,
      ...customBoxes,
    ];
    const granitIds = allPieces
      .map((el) => el.granit?.id)
      .filter((el) => el !== undefined);
    const uniqueIds = [...new Set(granitIds)];

    const granitsObject = uniqueIds?.map((el: string) => {
      const granit = allPieces?.find(
        (element) => element.granit?.id === el,
      )?.granit;

      return {
        id: el,
        granit,
      };
    });

    return granitsObject;
  }, [activePieces, customSteles, customPlatings, customSoles, customBoxes]);
};

export const useFilteredGranits = (
  granits: Granit[],
  pieceIndex: number,
  activePieces: any[],
  filter: string,
  origin: string[],
  categories: string[],
  seeAssemblyStock: boolean,
  assemblyStockData: any[],
) => {
  return useMemo(() => {
    let granitsToFilter = granits;

    // Si la pièce est INOX, filtrer pour n'avoir que les granits INOX
    if (pieceIndex < activePieces.length) {
      granitsToFilter = granitsToFilter.filter(
        (el) => el.material === activePieces[pieceIndex]?.material,
      );
    }

    if (pieceIndex < activePieces.length) {
      granitsToFilter = granitsToFilter.filter((el) => {
        if (activePieces[pieceIndex]?.underAssembly) {
          return activePieces[pieceIndex].underAssembly?.fabrications?.includes(
            el.fabrication,
          );
        }
        return activePieces[pieceIndex]?.fabrications?.includes(el.fabrication);
      });
    }
    const filtered = granitsToFilter.filter((el) => {
      if (origin.length > 0 && !origin.includes(el.fabrication)) {
        if (!origin.includes('IMPORT') && el.fabrication !== 'FRENCH')
          return false;
        if (!origin.includes('FRENCH') && el.fabrication === 'FRENCH')
          return false;
      }
      return (
        el.name
          ?.toLowerCase()
          .replace(' ', '')
          .includes(filter.toLocaleLowerCase().replace(' ', '')) &&
        categories.every((category) => el.categories.includes(category))
      );
    });

    if (seeAssemblyStock) {
      const assemblyGranits = [
        ...new Set(activePieces.map((el) => el.granit?.id)),
      ];

      const selectedGranit = activePieces[pieceIndex]?.granit;
      const notSelectedGranits = assemblyGranits.filter(
        (el) => el !== selectedGranit?.id,
      );

      const notSelectedGranitsPieces = activePieces.filter((el) =>
        notSelectedGranits.includes(el.granit?.id),
      );

      const selectedGranitsPieces = activePieces.filter(
        (el) => el.granit?.id === selectedGranit?.id,
      );

      if (notSelectedGranits.length > 1) {
        return [];
      }

      const availableStocks = assemblyStockData?.filter((el) => {
        const assemblyStockGranits = [
          ...new Set(
            el.stockPieces?.map(
              (element: { granit: { id: any } }) => element.granit?.id,
            ),
          ),
        ];

        if (assemblyStockGranits.length > 2) {
          return false;
        }

        const stockGranits = [
          ...new Set(
            el.stockPieces?.map(
              (stockPiece: { granitId: any }) => stockPiece.granitId,
            ),
          ),
        ];

        if (
          stockGranits.every((stockGranit) =>
            assemblyGranits.includes(stockGranit),
          )
        ) {
          return false;
        }

        const hasTheSameNotSelectedGranits = notSelectedGranitsPieces.every(
          (notSelectedGranitsPiece) => {
            const stockPiece = el.stockPieces?.find(
              (p: { pieceId: any }) => p.pieceId === notSelectedGranitsPiece.id,
            );

            if (!stockPiece) {
              return false;
            }

            return stockPiece.granitId === notSelectedGranitsPiece.granit?.id;
          },
        );

        const hasTheSameSelectedGranit = selectedGranitsPieces.every(
          (selectedGranitsPiece) => {
            const stockPiece = el.stockPieces?.find(
              (p: { pieceId: any }) => p.pieceId === selectedGranitsPiece.id,
            );

            if (!stockPiece) {
              return false;
            }

            if (
              notSelectedGranits[0] &&
              selectedGranitsPiece.granit?.id === notSelectedGranits[0].id
            ) {
              return false;
            }
            return true;
          },
        );

        return hasTheSameNotSelectedGranits && hasTheSameSelectedGranit;
      });

      const availableGranits = new Set(
        availableStocks
          .flatMap((el) =>
            el.stockPieces?.map((p: { granitId: any }) => p.granitId),
          )
          .filter((el) => el !== activePieces[pieceIndex]?.granit?.id),
      );

      return filtered.filter(
        (el) => availableGranits.has(el.id) && el.id !== notSelectedGranits[0],
      );
    }
    return filtered;
  }, [
    pieceIndex,
    activePieces,
    granits,
    filter,
    origin,
    categories,
    assemblyStockData,
    seeAssemblyStock,
  ]);
};

export const useSortedGranits = (filteredGranits: Granit[], sort: SortType) => {
  return useMemo(() => {
    return filteredGranits.sort((a, b) => {
      if (sort === 'ascending_name') {
        return a.name.localeCompare(b.name);
      }
      if (sort === 'descending_name') {
        return b.name.localeCompare(a.name);
      }
      if (sort === 'ascending_price') {
        return a.volumePrice - b.volumePrice;
      }
      if (sort === 'descending_price') {
        return b.volumePrice - a.volumePrice;
      }
      return 0;
    });
  }, [sort, filteredGranits]);
};
